// src/components/CharacterVoiceAssignment.tsx
// Updated: 2026-08-20 14:06:46 CDT
// Purpose: Assign active administrator-managed voices and restore saved assignments.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import {
  Loader2,
  Pencil,
  Play,
  Sparkles,
  Square,
  X,
  Users,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { detectGender } from "@/utils/characterAnalysis";
import { supabase } from "@/integrations/supabase/client";
import type { Character } from "@/types/screenplay";
import { toast } from "sonner";
import {
  assessVoiceAssignments,
  requiredVoiceCharacters,
  sanitizeVoiceAssignments,
} from "@/utils/voiceAssignments";
import {
  remapVoiceAssignment,
  renameScreenplayCharacter,
} from "@/utils/characterCorrections";
import type { CastingSuggestion } from "../../supabase/functions/_shared/voice-casting";
import VoiceCatalogPanel from "@/components/VoiceCatalogPanel";
import VoiceAssignmentWorkspace from "@/components/VoiceAssignmentWorkspace";
import { useSavedVoicePreview } from "@/hooks/useSavedVoicePreview";
import { catalogVoiceName, type CatalogVoice as VoiceRow } from "@/utils/voiceCatalog";
import { loadStoredVoiceCatalog } from "@/utils/storedVoiceCatalog";
import { characterSentenceCounts } from "@/utils/characterSentenceCounts";
import { VOICE_DRAG_TYPE } from "@/utils/voiceDrag";

interface CharacterVoiceAssignmentProps {
  onCommit?: () => void;
  commitLabel?: string;
  committing?: boolean;
  screenplayId?: string;
  onBeforeAnalysis?: () => Promise<boolean>;
  characters?: Character[];
  initialAssignments?: Record<string, string>;
  onVoiceAssignment: (assignments: Record<string, string>) => void;
  onCharactersChange?: (characters: Character[]) => void;
  onAssignmentReadinessChange?: (
    isComplete: boolean,
    remainingCharacters: string[],
  ) => void;
}

const CharacterVoiceAssignment = ({
  onCommit, commitLabel = "Assign voices", committing = false,
  screenplayId,
  onBeforeAnalysis,
  characters,
  initialAssignments = {},
  onVoiceAssignment,
  onCharactersChange,
  onAssignmentReadinessChange,
}: CharacterVoiceAssignmentProps) => {
  const storyHeadingId = useId();
  const charactersToUse = useMemo(() => {
    const sourceCharacters = characters ?? [];

    return requiredVoiceCharacters(sourceCharacters).map(
      (name) =>
        sourceCharacters.find((character) => character.name === name) ?? {
          name,
          lines: 0,
          description: "Narrator voice for action lines and scene descriptions",
          dialogue: [],
        },
    );
  }, [characters]);

  const [voiceAssignments, setVoiceAssignments] = useState<Record<string, string>>(
    initialAssignments,
  );
  const [availableVoices, setAvailableVoices] = useState<VoiceRow[]>([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(true);
  const [hasLoadedVoiceCatalog, setHasLoadedVoiceCatalog] = useState(false);
  const { voiceId: previewingVoiceId, status: previewStatus, error: previewError,
    playOrStop: previewVoice, stop: stopPreview } = useSavedVoicePreview();
  const [characterBeingRenamed, setCharacterBeingRenamed] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [castingBusy, setCastingBusy] = useState(false);
  const [castingTarget, setCastingTarget] = useState<string | null>(null);
  const [castingError, setCastingError] = useState('');
  const [castingMessage, setCastingMessage] = useState('');
  const [castingUndo, setCastingUndo] = useState<Record<string, string> | null>(null);
  const castingFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const castingSnapshot = JSON.stringify([screenplayId, charactersToUse.map(c => c.name), voiceAssignments, availableVoices.map(v => v.id)]);
  const latestCasting = useRef(castingSnapshot); latestCasting.current = castingSnapshot;
  useEffect(() => { setCastingUndo(null); setCastingError(''); setCastingMessage(''); }, [screenplayId, characters]);
  const sentenceCounts = useMemo(() => characterSentenceCounts(characters ?? []), [characters]);
  const drag = useRef<{ voiceId: string; screenplayId?: string; characters: Character[] | undefined } | null>(null);
  const [draggingVoiceName, setDraggingVoiceName] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dropMessage, setDropMessage] = useState('');
  const endVoiceDrag = useCallback(() => { drag.current = null; setDraggingVoiceName(null); setDropTarget(null); }, []);
  useEffect(() => {
    endVoiceDrag(); setDropMessage('');
    return () => { drag.current = null; };
  }, [screenplayId, characters, endVoiceDrag]);
  const catalogRequest = useRef(0);
  const [selectedActor, setSelectedActor] = useState<VoiceRow | null>(null);
  useEffect(() => { setSelectedActor(null); }, [screenplayId]);

  const loadVoices = useCallback(async () => {
    const request = ++catalogRequest.current;
    stopPreview();
    setIsLoadingVoices(true);

    try {
      const voices = await loadStoredVoiceCatalog<VoiceRow>(supabase,
        "id,name,display_name,provider,language,tags,characteristics,gender,is_active,sample_audio_url,provider_metadata,test_phrase,updated_at");
      if (request !== catalogRequest.current) return;
      setAvailableVoices(voices);
      setHasLoadedVoiceCatalog(true);

      console.debug("[CharacterVoiceAssignment] Active voices loaded:", {
        count: voices.length,
        ids: voices.map((voice) => voice.id),
      });

      if (voices.length === 0) {
        toast.info("No active voices are available yet.");
      }
    } catch (error) {
      if (request !== catalogRequest.current) return;
      console.error("[CharacterVoiceAssignment] Voice loading failed:", error);
      toast.error(
        error instanceof Error
          ? `Unable to load voices: ${error.message}`
          : "Unable to load voices.",
      );
    } finally {
      if (request === catalogRequest.current) setIsLoadingVoices(false);
    }
  }, [stopPreview]);

  useEffect(() => {
    void loadVoices();
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const voiceCatalogChannel = supabase
      .channel("public-active-voice-catalog")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "voices",
        },
        () => {
          // Coalesce bulk admin imports into one complete database refresh.
          clearTimeout(refreshTimer);
          refreshTimer = setTimeout(() => void loadVoices(), 250);
        },
      )
      .subscribe();

    return () => {
      catalogRequest.current++;
      clearTimeout(refreshTimer);
      void supabase.removeChannel(voiceCatalogChannel);
    };
  }, [loadVoices]);

  useEffect(() => {
    setVoiceAssignments(initialAssignments);
  }, [initialAssignments]);

  const availableVoiceIds = useMemo(
    () => new Set(availableVoices.map((voice) => voice.id)),
    [availableVoices],
  );

  const assignmentReadiness = useMemo(
    () =>
      assessVoiceAssignments(
        characters ?? [],
        voiceAssignments,
        availableVoiceIds,
      ),
    [availableVoiceIds, characters, voiceAssignments],
  );

  useEffect(() => {
    if (isLoadingVoices || !hasLoadedVoiceCatalog) {
      return;
    }

    const sanitizedAssignments = sanitizeVoiceAssignments(
      characters ?? [],
      voiceAssignments,
      availableVoiceIds,
      { preserveUnavailable: true },
    );

    if (JSON.stringify(sanitizedAssignments) !== JSON.stringify(voiceAssignments)) {
      console.warn("[CharacterVoiceAssignment] Removed stale assignments:", {
        previousCount: Object.keys(voiceAssignments).length,
        retainedCount: Object.keys(sanitizedAssignments).length,
      });
      setVoiceAssignments(sanitizedAssignments);
      onVoiceAssignment(sanitizedAssignments);
    }
  }, [
    availableVoiceIds,
    characters,
    hasLoadedVoiceCatalog,
    isLoadingVoices,
    onVoiceAssignment,
    voiceAssignments,
  ]);

  useEffect(() => {
    if (isLoadingVoices || !hasLoadedVoiceCatalog) {
      return;
    }

    onAssignmentReadinessChange?.(
      assignmentReadiness.isComplete,
      [
        ...assignmentReadiness.missingCharacters,
        ...assignmentReadiness.unavailableCharacters,
      ],
    );
  }, [
    assignmentReadiness,
    hasLoadedVoiceCatalog,
    isLoadingVoices,
    onAssignmentReadinessChange,
  ]);

  const voiceName = (voice: VoiceRow | undefined) =>
    voice ? catalogVoiceName(voice) : '';

  const handleVoiceAssignment = (character: string, voiceId: string) => {
    if (committing || !availableVoiceIds.has(voiceId) || !charactersToUse.some(c => c.name === character)) return;
    if (voiceAssignments[character] === voiceId) return;
    setCastingUndo(null); setCastingMessage('');
    const assignments = {
      ...voiceAssignments,
      [character]: voiceId,
    };

    setVoiceAssignments(assignments);
    onVoiceAssignment(assignments);

    console.debug("[CharacterVoiceAssignment] Voice assigned:", {
      character,
      voiceId,
    });
  };

  const validVoiceDrag = (event: DragEvent) => Boolean(drag.current && !isLoadingVoices &&
    drag.current.screenplayId === screenplayId && drag.current.characters === characters &&
    availableVoiceIds.has(drag.current.voiceId) && event.dataTransfer.types.includes(VOICE_DRAG_TYPE));
  const removeVoiceAssignment = (character: string) => {
    if (committing || isLoadingVoices || !voiceAssignments[character]) return;
    if (previewingVoiceId === voiceAssignments[character]) stopPreview();
    const assignments = { ...voiceAssignments };
    delete assignments[character];
    setCastingUndo(null); setCastingMessage('');
    setVoiceAssignments(assignments);
    onVoiceAssignment(assignments);
    setDropMessage(`Voice actor assignment removed from ${character}.`);
  };
  const startVoiceDrag = (voice: VoiceRow, event: DragEvent<HTMLButtonElement>) => {
    if (isLoadingVoices || !availableVoiceIds.has(voice.id)) { event.preventDefault(); return; }
    event.dataTransfer.setData(VOICE_DRAG_TYPE, voice.id);
    event.dataTransfer.effectAllowed = 'copy';
    drag.current = { voiceId: voice.id, screenplayId, characters };
    setDraggingVoiceName(catalogVoiceName(voice)); setDropMessage('');
  };
  const dropVoice = (character: string, event: DragEvent) => {
    if (!validVoiceDrag(event)) { endVoiceDrag(); return; }
    event.preventDefault(); event.stopPropagation();
    const voiceId = event.dataTransfer.getData(VOICE_DRAG_TYPE);
    if (voiceId === drag.current?.voiceId && charactersToUse.some(c => c.name === character)) {
      const voice = availableVoices.find(v => v.id === voiceId)!;
      const unchanged = voiceAssignments[character] === voiceId;
      handleVoiceAssignment(character, voiceId);
      setDropMessage(`${catalogVoiceName(voice)} ${unchanged ? 'is already assigned' : 'assigned'} to ${character}.`);
    }
    endVoiceDrag();
  };

  const openCharacterRename = (characterName: string) => {
    if (characterName === 'SCENE NARRATOR') return;
    setCharacterBeingRenamed(characterName);
    setRenameValue(characterName);
  };

  const closeCharacterRename = () => {
    setCharacterBeingRenamed(null);
    setRenameValue("");
  };

  const saveCharacterRename = () => {
    if (!characterBeingRenamed || characterBeingRenamed === 'SCENE NARRATOR' || !onCharactersChange) {
      return;
    }

    try {
      const correctedCharacters = renameScreenplayCharacter(
        characters ?? [],
        characterBeingRenamed,
        renameValue,
      );
      const correctedAssignments = remapVoiceAssignment(
        voiceAssignments,
        characterBeingRenamed,
        renameValue,
      );

      onCharactersChange(correctedCharacters);
      setVoiceAssignments(correctedAssignments);
      onVoiceAssignment(correctedAssignments);
      console.info("[CharacterVoiceAssignment] Character correction saved:", {
        previousName: characterBeingRenamed,
        correctedName: renameValue.trim(),
      });
      toast.success("Character correction saved.");
      closeCharacterRename();
    } catch (error) {
      console.error("[CharacterVoiceAssignment] Character correction failed:", {
        previousName: characterBeingRenamed,
        requestedName: renameValue,
        error,
      });
      toast.error(
        error instanceof Error ? error.message : "Unable to rename the character.",
      );
    }
  };

  const getCharacterGenderIndicator = (characterName: string) => {
    const gender = detectGender(characterName);

    if (gender === "male") return "♂";
    if (gender === "female") return "♀";
    return null;
  };

  const getGenderColor = (characterName: string) => {
    const gender = detectGender(characterName);

    if (gender === "male") return "text-blue-600";
    if (gender === "female") return "text-pink-600";
    return "text-gray-500";
  };

  const autoAssign = async (targetRole?: string) => {
    if (castingFlight.current || !screenplayId || !onBeforeAnalysis || !availableVoices.length) return;
    castingFlight.current = true; setCastingBusy(true); setCastingTarget(targetRole ?? null); setCastingError(''); setCastingMessage('');
    const snapshot = latestCasting.current;
    const current = () => mounted.current && latestCasting.current === snapshot;
    try {
      if (!(await onBeforeAnalysis())) throw new Error('Save the screenplay successfully before using Auto-Assign.');
      if (!current()) throw new Error('The character list or casting changed. No request was sent.');
      const names = charactersToUse.map(c => c.name);
      const response = await supabase.functions.invoke('voice-casting', { body: {
        action: 'assign_names', screenplay_id: screenplayId, character_names: names,
      } });
      if (response.error) {
        let message = 'Auto-Assign is unavailable. Existing assignments are unchanged.';
        if (response.error.context instanceof Response) {
          const body = await response.error.context.json().catch(() => null);
          if (typeof body?.error === 'string') message = body.error;
        }
        throw new Error(message);
      }
      if (!current()) throw new Error('Casting changed during Auto-Assign. The returned choices were not applied.');
      const suggestions = response.data?.suggestions as CastingSuggestion[];
      const narratorUnavailable = response.data?.narrator_unavailable === true;
      if (!Array.isArray(suggestions) || suggestions.length !== names.length || new Set(suggestions.map(s => s.role)).size !== names.length ||
        suggestions.some(s => !names.includes(s.role) || (s.voice_id === null && s.role === 'SCENE NARRATOR' && narratorUnavailable ? false : !s.voice_id || !availableVoiceIds.has(s.voice_id))))
        throw new Error('Incomplete casting choices. Existing assignments are unchanged.');
      // Keep the server's complete saved-name validation/cache, but apply only
      // the requested role. Never replace the other draft assignments.
      const assigned = { ...voiceAssignments, ...Object.fromEntries(suggestions
        .filter(s => s.voice_id && (!targetRole || s.role === targetRole)).map(s => [s.role, s.voice_id!])) };
      setCastingUndo(voiceAssignments); setVoiceAssignments(assigned); onVoiceAssignment(assigned);
      if (narratorUnavailable && (!targetRole || targetRole === 'SCENE NARRATOR')) setCastingError('Scene Narrator unchanged: select at least one active voice’s Narrator checkbox in the admin console.');
    } catch (error) {
      if (mounted.current && latestCasting.current === snapshot) setCastingError(error instanceof Error ? error.message : 'Auto-Assign failed. Existing casting is unchanged.');
    } finally { castingFlight.current = false; if (mounted.current) { setCastingBusy(false); setCastingTarget(null); } }
  };

  return (
    <Card className="voice-assignment-panel min-w-0 w-full">
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>Assign Voices to Characters</span>
            </CardTitle>
            <p className="mt-2 text-xs text-muted-foreground">Drag a voice actor name onto a character to assign it.</p>
            <p role="status" aria-live="polite" className="sr-only">{dropMessage || (draggingVoiceName ? `Drag ${draggingVoiceName} onto a character.` : '')}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <Button
              onClick={() => void autoAssign()}
              variant="outline"
              size="sm"
              disabled={committing || castingBusy || isLoadingVoices || availableVoices.length === 0 || !screenplayId || !onBeforeAnalysis}
              className="flex items-center space-x-2"
              title="Suggest casting from character names using OpenAI"
            >
              {castingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              <span>{castingBusy ? 'Assigning…' : 'Auto-Assign'}</span>
            </Button>
            {onCommit && <Button size="sm" onClick={onCommit} disabled={committing || castingBusy}>
              {committing ? 'Checking audio…' : commitLabel}
            </Button>}
            {castingUndo && <Button variant="ghost" size="sm" disabled={committing || castingBusy} onClick={() => {
              setVoiceAssignments(castingUndo); onVoiceAssignment(castingUndo); setCastingUndo(null); setCastingMessage('Previous casting restored.');
            }}>Undo auto-assignment</Button>}
          </div>
        </div>
        {castingError && <p role="alert" className="text-sm text-destructive">{castingError}</p>}
        {castingMessage && <p role="status" className="text-sm text-muted-foreground">{castingMessage}</p>}
      </CardHeader>

      <CardContent className="min-w-0">
        <VoiceAssignmentWorkspace hasCatalog={availableVoices.length > 0} draggingVoice={Boolean(draggingVoiceName)}>
        <div className={`min-w-0 pt-4 ${availableVoices.length > 0 ? 'col-start-2 row-start-1' : ''}`}>
        <h3 id={storyHeadingId} className="mb-2 px-4 text-sm font-medium">Story characters</h3>
        {isLoadingVoices ? (
          <div
            className="flex items-center justify-center gap-2 py-12 text-muted-foreground"
            role="status"
          >
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading voices…
          </div>
        ) : availableVoices.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="font-medium">No active voices are available.</p>
            <p className="mt-2 text-sm text-muted-foreground">
              An administrator must create and activate a voice before assignments can be made.
            </p>
          </div>
        ) : (
          <div role="region" aria-labelledby={storyHeadingId} tabIndex={0}
            className="voice-assignment-characters voice-assignment-scroll space-y-4 overflow-y-auto overscroll-y-contain rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {charactersToUse.map((character) => (
              <div
                key={character.name}
                role="group" aria-label={`Voice assignment for ${character.name}`}
                onDragOver={event => { if (validVoiceDrag(event)) { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; setDropTarget(character.name); } }}
                onDragLeave={event => { if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDropTarget(previous => previous === character.name ? null : previous); }}
                onDrop={event => dropVoice(character.name, event)}
                className={`flex flex-col gap-4 rounded-lg border bg-card p-4 transition-colors ${dropTarget === character.name ? 'border-primary bg-primary/10 ring-2 ring-primary' : draggingVoiceName ? 'border-primary/50 border-dashed' : ''}`}
              >
                <div className="flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-3">
                    <h3 className="flex items-center space-x-2 font-mono text-base font-medium">
                      <span>{character.name}</span>
                      <span className={`text-base ${getGenderColor(character.name)}`}>
                        {getCharacterGenderIndicator(character.name)}
                      </span>
                    </h3>
                    {character.name !== 'SCENE NARRATOR' && onCharactersChange && characters?.some(
                      (candidate) => candidate.name === character.name,
                    ) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openCharacterRename(character.name)}
                        aria-label={`Correct ${character.name}`}
                        title="Correct character name or merge with another character"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Badge
                      variant="secondary"
                      className="text-xs"
                      title="Sentences in this role’s text. Unpunctuated fragments and scene headings count as one; silent directions and pauses are excluded."
                    >
                      {sentenceCounts[character.name] ?? 0} {(sentenceCounts[character.name] ?? 0) === 1 ? 'sentence' : 'sentences'}
                    </Badge>
                  </div>
                  {character.name === 'SCENE NARRATOR' && <p className="text-sm text-muted-foreground">
                    {character.description}
                  </p>}
                </div>

                <div className="flex items-center gap-2">
                  <button type="button" className="min-h-10 min-w-0 flex-1 rounded-md border border-dashed border-input bg-background px-3 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Voice for ${character.name}: ${voiceName(availableVoices.find(v => v.id === voiceAssignments[character.name])) || 'unassigned'}`}
                    title={selectedActor ? `Assign ${catalogVoiceName(selectedActor)} to ${character.name}` : 'Drag an actor here, or select an actor and click here'}
                    disabled={committing}
                    onClick={() => {
                      if (selectedActor && availableVoiceIds.has(selectedActor.id)) {
                        handleVoiceAssignment(character.name, selectedActor.id);
                        setDropMessage(`${catalogVoiceName(selectedActor)} assigned to ${character.name}.`);
                      } else toast.info('Select a voice actor from the list, then click this assignment.');
                    }}>
                    {voiceAssignments[character.name]
                      ? voiceName(availableVoices.find(v => v.id === voiceAssignments[character.name])) || 'Voice unavailable — drag a voice actor here'
                      : 'Drag voice actor name here'}
                  </button>

                  {voiceAssignments[character.name] && (() => {
                    const assignedVoice = availableVoices.find(
                      (voice) => voice.id === voiceAssignments[character.name],
                    );

                    if (!assignedVoice) return null;

                    return (
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() => void previewVoice(assignedVoice)}
                        disabled={!assignedVoice.sample_audio_url}
                        aria-label={`${previewingVoiceId === assignedVoice.id ? "Stop preview of" : "Preview"} ${voiceName(assignedVoice)}`}
                        title={
                          assignedVoice.sample_audio_url
                            ? `Preview ${voiceName(assignedVoice)}`
                            : "Preview sample not generated"
                        }
                      >
                        {previewingVoiceId === assignedVoice.id ? (
                          <Square className="h-3 w-3" />
                        ) : (
                          <Play className="h-3 w-3" />
                        )}
                      </Button>
                    );
                  })()}
                  <Button type="button" size="icon" variant="outline"
                    disabled={committing || castingBusy || isLoadingVoices || !availableVoices.length || !screenplayId || !onBeforeAnalysis}
                    onClick={() => void autoAssign(character.name)}
                    aria-label={`Auto-assign voice for ${character.name}`}
                    title="Auto-assign this character only">
                    {castingBusy && castingTarget === character.name
                      ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                      : <Sparkles aria-hidden="true" className="h-4 w-4" />}
                  </Button>
                  {voiceAssignments[character.name] && <Button
                    type="button" size="icon" variant="outline"
                    disabled={committing}
                    onClick={() => removeVoiceAssignment(character.name)}
                    aria-label={`Remove voice assignment for ${character.name}`}
                    title="Remove voice actor assignment"
                  >
                    <X aria-hidden="true" className="h-4 w-4" />
                  </Button>}
                </div>
                {dropTarget === character.name && <p className="text-xs font-medium text-primary">Drop {draggingVoiceName} here</p>}
              </div>
            ))}
          </div>
        )}

        </div>
        {availableVoices.length > 0 && <div className="order-first min-w-0 self-stretch col-start-1 row-start-1">
          <VoiceCatalogPanel voices={availableVoices} previewingVoiceId={previewingVoiceId} defaultCountry="United States"
            previewStatus={previewStatus} previewError={previewError}
            onPreview={voice => void previewVoice(voice)} onStop={stopPreview}
            onVoiceDragStart={startVoiceDrag} onVoiceDragEnd={endVoiceDrag} onSelectActor={setSelectedActor} />
        </div>}
        </VoiceAssignmentWorkspace>
      </CardContent>

      <Dialog
        open={characterBeingRenamed !== null}
        onOpenChange={(open) => {
          if (!open) closeCharacterRename();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correct character name</DialogTitle>
            <DialogDescription>
              This updates every matching line in the ordered screenplay. Enter an existing name to merge the characters, or use SCENE NARRATOR to classify the lines as narration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="corrected-character-name">Character name</Label>
            <Input
              id="corrected-character-name"
              value={renameValue}
              maxLength={80}
              onChange={(event) => setRenameValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  saveCharacterRename();
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeCharacterRename}>
              Cancel
            </Button>
            <Button type="button" onClick={saveCharacterRename}>
              Save correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CharacterVoiceAssignment;
